import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { ADOBE_EXCHANGE_URL } from '../constants/urls';
import { pollLicenseBySession } from '../api/axios';
import './Success.css';
import posthog from 'posthog-js';

const POLL_MS = 2000;
/** `?demo_key=1` — show a fake key in the “ready” UI; skips polling. */
const PLACEHOLDER_LICENSE_KEY = 'SS-DEMO-7A2B-9C4D-1E6F-8A0B-2C4D-6E8F';
/** Set to `true` to preview the key box without a query param; set back to `false` after. */
const PREVIEW_PLACEHOLDER_LICENSE = false;

const Success: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const showPlaceholderKey =
    PREVIEW_PLACEHOLDER_LICENSE || searchParams.get('demo_key') === '1';

  const [licenseKey, setLicenseKey] = useState<string | null>(() =>
    showPlaceholderKey ? PLACEHOLDER_LICENSE_KEY : null
  );
  const [licensePhase, setLicensePhase] = useState<'idle' | 'loading' | 'ready'>(() => {
    if (showPlaceholderKey) {
      return 'ready';
    }
    return searchParams.get('session_id') ? 'loading' : 'idle';
  });
  const [copyHint, setCopyHint] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (showPlaceholderKey) {
      return;
    }
    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void })
      .gtag;
    if (typeof window !== 'undefined' && gtag) {
      gtag('event', 'conversion_event_purchase', {});
    }
    posthog.capture('payment_successful', {
      has_session_id: Boolean(sessionId),
    });
  }, [sessionId, showPlaceholderKey]);

  const clearPollInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (showPlaceholderKey) {
      return;
    }
    if (!sessionId) {
      setLicensePhase('idle');
      return;
    }

    setLicensePhase('loading');
    setLicenseKey(null);

    const run = async () => {
      const result = await pollLicenseBySession(sessionId);
      if (result.state === 'ready') {
        setLicenseKey(result.key);
        setLicensePhase('ready');
        posthog.capture('license_key_shown', { source: 'success_polling' });
        clearPollInterval();
        return;
      }
    };

    void run();
    intervalRef.current = setInterval(() => {
      void run();
    }, POLL_MS);

    return () => {
      clearPollInterval();
    };
  }, [sessionId, showPlaceholderKey, clearPollInterval]);

  const handleCopy = async () => {
    if (!licenseKey) {
      return;
    }
    try {
      await navigator.clipboard.writeText(licenseKey);
      setCopyHint(true);
      setTimeout(() => setCopyHint(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <motion.div
      className="success-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="success-container success-container-wide">
        <header className="success-intro">
          <motion.div
            className="success-icon"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.05, duration: 0.45, ease: 'easeOut' }}
            aria-hidden
          >
            <svg
              width="72"
              height="72"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="#10B981"
                stroke="#10B981"
                strokeWidth="2"
              />
              <path
                d="M8 12l2 2 4-4"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
          <motion.h1
            className="success-title"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.45 }}
          >
            You&apos;re all set!
          </motion.h1>
          <motion.p
            className="success-message"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.45 }}
          >
            Your Pro subscription is active. You can return to Premiere Pro and
            continue using Substyle with your monthly credits.
          </motion.p>
        </header>

        <section
          className="success-block success-install"
          aria-labelledby="install-heading"
        >
          <h2 className="success-block-title" id="install-heading">
            How to install
          </h2>
          <ol className="success-install-steps">
            <li>
              Install the extension from{' '}
              <a
                href={ADOBE_EXCHANGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="success-inline-link"
              >
                Adobe Exchange
              </a>
              {' '}
              if you haven&apos;t already.
            </li>
            <li>
              In Adobe Premiere Pro, go to <strong>Window</strong> →{' '}
              <strong>Extensions</strong> → <strong>Substyle</strong>.
            </li>
            <li>
              You&apos;re ready — Pro includes 10,000 credits each month. If you
              need a license key, check below or your email inbox.
            </li>
          </ol>
        </section>

        <section
          className="success-block success-license"
          aria-labelledby="license-heading"
        >
          <h2 className="success-block-title" id="license-heading">
            Your license key
          </h2>

          {sessionId == null && !showPlaceholderKey && (
            <p className="success-block-text">
              Check your email inbox for your license key.
            </p>
          )}

          {sessionId && !showPlaceholderKey && licensePhase === 'loading' && (
            <div className="success-license-loading" role="status" aria-live="polite">
              <div className="success-license-loading-box">
                <div className="spinner" aria-hidden />
                <span className="visually-hidden">Loading your license key</span>
              </div>
              <p className="success-license-loading-hint">
                Key not loading? We also sent it to your email inbox.
              </p>
            </div>
          )}

          {licensePhase === 'ready' && licenseKey && (
            <div className="success-key-box">
              <code
                className="success-key-value"
                id="license-key-text"
                aria-label="License key"
              >
                {licenseKey}
              </code>
              <button
                type="button"
                className="success-copy-btn"
                onClick={handleCopy}
                aria-label="Copy license key"
              >
                {copyHint ? 'Copied' : 'Copy'}
              </button>
            </div>
          )}
        </section>

        <div className="success-info">
          <p>
            Need help?{' '}
            <a
              href="mailto:support@codec422.com"
              className="success-inline-link"
            >
              Contact support
            </a>
            .
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default Success;
